import { useEffect, useState, useRef } from "react";
import { Box, Button, TextField, Typography, Dialog, DialogActions, DialogContent } from "@mui/material";
import { useSearchParams, Link } from "react-router-dom";
import { useDispatch } from "react-redux";
import { Trans, useTranslation } from "react-i18next";
import LoadingButton from "@mui/lab/LoadingButton";
import { checkWorkShopAccessCode } from "@api/formApi";
import { getWorkshopListByWorkshopId } from "@api/enrollmentApi";
import { setAccessCode } from "@store/eventForm/eventFormSlice";
import { parseEventAccessCode } from "@hooks/useLocalStorage";
import "@styles/pages/EventForm/EventForm.scss";
import "@styles/pages/EventForm/EventAccessForm.scss";
import { set } from "date-fns";

const AccessCodeWrapper = ({ children, forceStoreLocalStorageWhenInitial=true }) => {
    const { t, i18n } = useTranslation();
    const dispatch = useDispatch();
    const inputRef = useRef(null);
    const firstDialogOpen = useRef(true);
    const [searchParams] = useSearchParams();
    const id = searchParams.get("id");
    const [isLoading, setIsLoading] = useState(false);
    const [isChecking, setIsChecking] = useState(true);
    const [accessCodeValid, setAccessCodeValid] = useState(false);
    const [accessCodeError, setAccessCodeError] = useState(null);
    const [specialErrorCta, setSpecialErrorCta] = useState(false);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [dialogData, setDialogData] = useState(null);

    useEffect(() => {
        const fetchAccessCode = async (code) => {
            try {
                const response = await checkWorkShopAccessCode({code, id});
                if(response && response.data && response.data.StatusCode === 200) {
                    setAccessCodeValid(true);
                }
            } catch (error) {
                console.error("Error checking access code:", error);
            } finally {
                setIsChecking(false);
            }
        }
        const code = parseEventAccessCode(id) || "";

        if (code) fetchAccessCode(code);
        else setIsChecking(false);
    }, [id]);

    const handleProcess = async () => {
        try {
            const response = await getWorkshopListByWorkshopId({workshop_id: id});
            if (response && response.data && response.data.StatusCode === 200) {
                setDialogData(response.data.Data);
                setDialogOpen(true);
            }
        }
        catch (error) {
            console.error("Error fetching workshop list:", error);
        }
    }

    const handleCloseDialog = () => {
        setDialogOpen(false);
    }

    const handleSubmit = async () => {
        setIsLoading(true);
        setAccessCodeError(null);
        setSpecialErrorCta(false);
        setDialogOpen(false);
        try {
            const response = await checkWorkShopAccessCode({ code: inputRef.current.value, id });
            setIsLoading(false);
            if(response && response.data && response.data.StatusCode === 200) {
                const savedAccessCode = localStorage.getItem("event_access_code") || "{}";
                const currentCodes = JSON.parse(savedAccessCode);
                currentCodes[id] = inputRef.current.value;
                if(forceStoreLocalStorageWhenInitial) {
                    localStorage.setItem("event_access_code", JSON.stringify(currentCodes));
                }
                else {
                    dispatch(setAccessCode(inputRef.current.value));
                }

                if(firstDialogOpen.current) {
                    handleProcess()
                }
                else {
                    setAccessCodeValid(true);
                }
            }
            else {
                const status = response && response.data && response.data.StatusCode;
                switch(status) {
                    case 709:
                        setAccessCodeError("form:access_code_only_linkup_or_sa");
                        setSpecialErrorCta(true);
                        break;
                    case 403:
                        setAccessCodeError("form:access_code_expired");
                        break;
                    case 402:
                        setAccessCodeError("form:access_code_in_used");
                        break;
                    case 400, 401:
                        setAccessCodeError("form:access_code_invalid");
                        break;
                    default:
                        setAccessCodeError("form:access_code_invalid");
                }
            }
        } catch (error) {
            console.error("Error checking access code:", error);
            setIsLoading(false);
        }
    }

    if(accessCodeValid) {
        return <>{children}</>;
    }

    if(isChecking) {
        return null;
    }

    return (
        <>
            <Dialog open={dialogOpen} onClose={handleCloseDialog}>
                <DialogContent style={{textAlign: 'center'}}>

                    <Typography
                        dangerouslySetInnerHTML={{
                        __html: t("form:access_code_dialog_message", {
                            workshop: dialogData ? dialogData[`title_${i18n.language}`] : "Workshop"
                        }),
                        }}
                    />
                </DialogContent>
                <DialogActions style={{justifyContent: 'center'}}>
                    <Button
                        className=""
                        variant="outlined"
                        onClick={handleCloseDialog}
                    >
                        <Trans i18nKey="generic:back" />
                    </Button>

                        <LoadingButton
                            className="loading-button"
                            disabled={isLoading}
                            loading={isLoading}
                            onClick={() => setAccessCodeValid(true)}
                        >
                            <Trans i18nKey="generic:continue" />
                        </LoadingButton>
                </DialogActions>
            </Dialog>
            <Box className="event-form-container access-code-page">
                <Box className="row-container">
                    <Box className="field-container">
                        <Typography>
                            <span className="red">*</span>
                            <Trans i18nKey="form:access_code" />
                        </Typography>
                        <TextField
                            inputRef={inputRef}
                            name="accessCode"
                            fullWidth
                            variant="outlined"
                        />
                        {accessCodeError && (
                            <Typography className="error-text">
                                <Trans i18nKey={accessCodeError} />
                            </Typography>
                        )}
                        {
                            specialErrorCta && (
                                <Box className="error-cta-container">
                                    <Link to="/member_zone" className="button">
                                        <Typography className="cta">
                                            <Trans i18nKey="form:access_code_only_linkup_or_sa_cta" />
                                        </Typography>
                                    </Link>
                                </Box>
                            )
                        }
                    </Box>
                    <Box className="next-button-container">
                        <LoadingButton
                            className="loading-button"
                            disabled={isLoading}
                            loading={isLoading}
                            onClick={handleSubmit}
                        >
                            <Trans i18nKey="form:next_button" />
                        </LoadingButton>
                    </Box>
                </Box>
            </Box>
        </>
    );
}

export default AccessCodeWrapper;